import { DoCheck, Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { interval, merge, Observable, Subject, Subscription } from 'rxjs';
import { EntityIdChangeMessage, FlowMessage, FlowState } from '../../../../shared/models/web-socket';
import { webSocket } from 'rxjs/webSocket';
import { HttpClient } from '@angular/common/http';
import { delay, map, repeatWhen, retryWhen, take, tap } from 'rxjs/operators';
import { UserDetails } from '../../../../shared/models/userDetails';
import { FlowUserDetails } from '../../../../shared/models/FlowUserDetails';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CountDownComponent } from '../../components/count-down/count-down.component';
import { OAuthService } from 'angular-oauth2-oidc';
import { SharedService } from '../../../../core/services/store/shared-store/shared-storage.service';

const pingInterval$ = interval(5000);
const retryInterval$ = interval(1000);

const subject: Subject<FlowMessage> = <Subject<FlowMessage>>(
  webSocket(environment.controlWebSocketUrl + '/V2')
);

@Injectable({
  providedIn: 'root',
})
export class FlowWebSocketsService {
  pingTime: Subscription;
  private _websocketFlowMessages$: Subscription;
  private countDownTime: number;
  private disconnectionCheck: boolean;
  private lockFlows: object = {};
  private dialogOpen = false;
  private entityId = this.sharedService.getEntityId();
  private entityType = this.sharedService.getEntityType();

  constructor(
    private http: HttpClient,
    private sharedService: SharedService,
    public dialog: MatDialog,
    private oauthService: OAuthService
  ) {
    // this.connect();
  }

  private connect() {
    this.countDownTime = 60;
    this.disconnectionCheck = true;
    this._websocketFlowMessages$ = this.webSocketSetup().subscribe((flowMessage: FlowMessage) => {
      this.getFlowMessage(flowMessage);
    });
  }

  get getLockFlows() {
    return this.lockFlows;
  }

  get getCountDownTime(): number {
    return this.countDownTime;
  }

  get getIsDisconnect(): boolean {
    return this.disconnectionCheck;
  }

  public webSocketSetup(): Observable<FlowMessage> {
    const messages = subject.pipe(
      retryWhen((errors) =>
        errors.pipe(
          delay(2000),
          tap(() => {
            this.connectionRetryState();
          })
        )
      ),
      repeatWhen((complete) =>
        complete.pipe(
          delay(2000),
          tap((err) => console.warn('Flow websocket connection dropped retrying', err))
        )
      ),
      tap(() => {})
    );

    const pingMessages = pingInterval$.pipe(
      map(() => {
        return {
          state: FlowState.PING,
          entityId: '',
          flowName: '',
          lockedFlows: {},
          accessToken: '',
        } as FlowMessage;
      }),
      tap((ping) => subject.next(ping))
    );

    return merge(messages, pingMessages);
  }

  public send(flowMessage: FlowMessage): void {
    subject.next(flowMessage);
  }

  getFlowMessage(flowMessage: FlowMessage) {
    flowMessage.accessToken = this.sharedService.getUserAccessToken;
    flowMessage.entityId = this.entityId;
    flowMessage.entityType = this.entityType;

    switch (flowMessage.state) {
      case FlowState.INIT:
        this.send(flowMessage);
        this.resetTimer();
        break;
      case FlowState.PING:
        flowMessage.state = FlowState.PING;
        this.send(flowMessage);
        break;
      case FlowState.BROADCAST:
        this.lockFlows = flowMessage.lockedFlows;
        break;
      case FlowState.PONG:
        break;
      case FlowState.LOGOUT:
        this.oauthService.logOut();
        break;
    }
  }

  entityChange() {
    this.updateEntity();
    const message: FlowMessage = EntityIdChangeMessage;
    message.entityId = this.entityId;
    message.entityType = this.entityType;
    message.accessToken = this.sharedService.getUserAccessToken;
    this.send(message);
  }

  private resetTimer() {
    if (this.dialogOpen) {
      this.countDownTime = 60;
      this.dialog.closeAll();
      this.disconnectionCheck = true;
      this.dialogOpen = false;
      this.pingTime.unsubscribe();
    }
  }

  isFlowLocked(flowRef: string): boolean {
    const userDetails = this.sharedService.getUserInfo() as UserDetails;
    const flowUserDetails: FlowUserDetails = this.lockFlows[flowRef];
    if (flowUserDetails) {
      return flowUserDetails.userId !== userDetails['id'];
    } else {
      return false;
    }
  }

  getFlowUserDetails(flowName: string): FlowUserDetails {
    return this.lockFlows[flowName];
  }

  lockFlow() {
    const flowMessage: FlowMessage = {
      lockedFlows: {},
      state: FlowState.LOCK_FLOW,
      accessToken: this.sharedService.getUserAccessToken,
      entityId: this.sharedService.getEntityId(),
      entityType: this.sharedService.getEntityType(),
      flowName: this.sharedService.getFlowRef,
    };

    this.send(flowMessage);
  }

  unLockFlow() {
    const flowMessage: FlowMessage = {
      lockedFlows: {},
      state: FlowState.UNLOCK_FLOW,
      accessToken: this.sharedService.getUserAccessToken,
      entityId: this.sharedService.getEntityId(),
      entityType: this.sharedService.getEntityType(),
      flowName: this.sharedService.getFlowRef,
    };

    this.send(flowMessage);
  }

  connectionRetryState(): void {
    if (this.disconnectionCheck) {
      this.dialogOpen = true;
      this.startWebSocketRetryTime();
      this.openTimeDialog();
      this.disconnectionCheck = false;
    }
  }

  private openTimeDialog() {
    const data = {
      time: this.countDownTime,
      isEditing: this.sharedService.getIsFlowChanged(),
    };

    const dialogRef = this.dialog.open(CountDownComponent, {
      width: '500px',
      data: data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result === 'reconnect') {
          this._websocketFlowMessages$.unsubscribe();
          this.connect();
        }
      },
      (error) => {
        console.warn('Dialog: CountDownComponent -> ', error);
      }
    );
  }

  private startWebSocketRetryTime() {
    this.pingTime = retryInterval$.subscribe(() => {
      this.countDownTime -= 1;
      if (this.countDownTime <= 0) {
        this._websocketFlowMessages$.unsubscribe();
        this.pingTime.unsubscribe();
      }
    });
  }

  getMyLockedFlowName() {
    for (const key in this.lockFlows) {
      const lockFlowUserDetails = this.lockFlows[key] as FlowUserDetails;
      if (lockFlowUserDetails.userId === this.sharedService.getUserInfo()['user_id']) {
        return key;
      }
    }
  }

  updateEntityId() {
    this.entityId = this.sharedService.getEntityId();
  }

  updateEntityType() {
    this.entityType = this.sharedService.getEntityType();
  }

  updateEntity() {
    this.updateEntityId();
    this.updateEntityType();
  }
}
